import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

/** Present only when this session was opened from the admin panel's
 *  "Quick Login" action. Logging out then sends the browser back there. */
export interface QuickLoginSession {
  returnUrl: string | null;
  issuedBy?: string | null;
}

/** Why a session ended without the user clicking "log out" on this device. */
export type SessionEndReason =
  | 'password_changed'   // password changed on another device
  | 'password_reset'     // password reset via email link
  | 'logout'             // this session was signed out elsewhere
  | 'admin'              // an admin changed the password
  | 'disabled'           // account disabled by an admin
  | 'expired';           // refresh token invalid / expired

const SESSION_END_MESSAGES: Record<SessionEndReason, string> = {
  password_changed: 'Your password was changed on another device. Please log in again.',
  password_reset:   'Your password was reset. Please log in with your new password.',
  logout:           'You have been signed out. Please log in again.',
  admin:            'Your password was changed by an administrator. Please log in again.',
  disabled:         'Your account has been disabled by an administrator.',
  expired:          'Your session has ended. Please log in again.',
};

/** sessionStorage key the login page reads to explain why the user is there. */
export const AUTH_NOTICE_KEY = 'authNotice';

export interface Auth {
  isLoggedIn: boolean;
  user: any;
  token?: string;
  refreshToken?: string;
  quickLogin?: QuickLoginSession | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private authSubject: BehaviorSubject<Auth>;
  auth$;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    // Initialize auth state safely for SSR
    this.authSubject = new BehaviorSubject<Auth>(this.getAuthFromStorage());
    this.auth$ = this.authSubject.asObservable();

    if (isPlatformBrowser(this.platformId)) {
      // Keep every tab of this browser in sync: logging out (or being signed
      // out) in one tab must not leave another tab looking logged in.
      window.addEventListener('storage', (e) => {
        if (e.key !== 'auth') return;
        const wasLoggedIn = this.authSubject.value?.isLoggedIn;
        const next = this.getAuthFromStorage();
        this.authSubject.next(next);
        if (wasLoggedIn && !next?.isLoggedIn) this.redirectToLogin();
      });
    }
  }

  private sessionEnding = false;

  /**
   * The server no longer accepts this device's session (password changed or
   * reset, signed out elsewhere, account disabled, refresh token expired).
   * Clears local auth and does a full page load to the login page, so no
   * cached data from the old session stays on screen. Safe to call repeatedly.
   */
  endSession(reason: SessionEndReason = 'expired', message?: string): void {
    if (!isPlatformBrowser(this.platformId) || this.sessionEnding) return;

    const wasLoggedIn = this.authSubject.value?.isLoggedIn || !!localStorage.getItem('auth');
    const redirectUrl = this.getLogoutRedirectUrl();
    this.clearAuth();
    if (!wasLoggedIn) return;

    this.sessionEnding = true;
    try {
      sessionStorage.setItem(AUTH_NOTICE_KEY, message || SESSION_END_MESSAGES[reason] || SESSION_END_MESSAGES.expired);
    } catch { /* storage unavailable — redirect anyway */ }
    this.redirectToLogin(redirectUrl);
  }

  private redirectToLogin(url = '/login'): void {
    if (url === '/login' && window.location.pathname.startsWith('/login')) return;
    window.location.assign(url);
  }

  private getAuthFromStorage(): Auth {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem('auth');
      try {
        return stored ? JSON.parse(stored) : { isLoggedIn: false, user: null };
      } catch {
        return { isLoggedIn: false, user: null };
      }
    } else {
      // Default value on server side (SSR)
      return { isLoggedIn: false, user: null };
    }
  }

  getClientId(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    let clientId = localStorage.getItem('clientId');
    if (!clientId) {
      if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        clientId = crypto.randomUUID();
      } else {
        clientId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }
      localStorage.setItem('clientId', clientId);
    }

    return clientId;
  }

  setAuth(auth: Auth) {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('auth', JSON.stringify(auth));
    }
    this.authSubject.next(auth);
  }

  getAuthSnapshot(): Auth {
    return this.authSubject.value;
  }

  updateUser(user: any) {
    const current = this.getAuthSnapshot();
    if (!current?.isLoggedIn) return;

    const next: Auth = {
      ...current,
      user: {
        ...(current.user ?? {}),
        ...(user ?? {}),
      },
    };

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('auth', JSON.stringify(next));
    }

    this.authSubject.next(next);
  }

  clearAuth() {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('auth');
    }
    this.authSubject.next({ isLoggedIn: false, user: null });
  }

  getToken(): string | null {
    return this.getAuthSnapshot()?.token ?? null;
  }

  /** True when the current session was started by an admin "Quick Login". */
  isQuickLoginSession(): boolean {
    return !!this.getAuthSnapshot()?.quickLogin;
  }

  /**
   * Where to send the browser after sign-out. Must be read BEFORE clearAuth().
   * Quick-login sessions go back to the admin panel page they came from;
   * everyone else goes to /login as before.
   */
  getLogoutRedirectUrl(): string {
    const returnUrl = this.getAuthSnapshot()?.quickLogin?.returnUrl;
    if (returnUrl && /^https?:\/\//i.test(returnUrl)) return returnUrl;
    return '/login';
  }
}
